import { CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import * as Fonts from "../../../../components/Fonts";
import { LoadingModal } from "../../../../components/LoadingModal";
import { SubHeader } from "../../../../components/SubHeader";
import { api } from "../../../../services/api";
import { isApiError } from "../../../../services/api/errors";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import {
  BillingDetailsValue,
  BillingErrors,
  BrazilAddress,
  BrazilDocument,
  EMPTY_BILLING,
  Field,
  validateBilling,
  withSavedBilling,
} from "../Checkout/BillingDetails";
import { CardIcon } from "../Checkout/icons";
import { PaymentMethod, PaymentMethods } from "../Checkout/PaymentMethods";
import { CardErrors, EMPTY_CARD, StripeProvider, validateCard } from "../Checkout/StripeCard";
import { FieldGrid } from "../Checkout/styles";
import { Wallet, WalletAvailability, WalletProbe, WalletSetupButton } from "../Checkout/WalletButton";
import { Actions, Container, Current, Section } from "./styles";
import { useManagedSubscription, useSave } from "./useManagedSubscription";

/**
 * How the subscription is paid from now on: another card (typed, or from Apple Pay / Google Pay), or, on the
 * yearly plan, Pix or boleto (the card stops being charged; the year already paid stays, and the next one
 * comes as a Pix/boleto). Pix/boleto plans renew through the renewal page instead.
 */
export const PaymentMethodPage = () => (
  <StripeProvider>
    <PaymentMethodContent />
  </StripeProvider>
);

const PaymentMethodContent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const prefix = "pages.subscription.manage.paymentMethod";
  const { lastCard, isAnnual, isLoading, detailUrl, siteId } = useManagedSubscription();
  const { state, error, save } = useSave(detailUrl, navigate);

  const [method, setMethod] = useState<PaymentMethod>();
  const [card, setCard] = useState(EMPTY_CARD);
  const [cardErrors, setCardErrors] = useState<CardErrors>({});
  const [billing, setBilling] = useState<BillingDetailsValue>(EMPTY_BILLING);
  const [billingErrors, setBillingErrors] = useState<BillingErrors>({});
  const [isBoletoBlocked, setBoletoBlocked] = useState(false);
  const [availableWallets, setAvailableWallets] = useState<WalletAvailability>({ applePay: false, googlePay: false });
  const [stripeError, setStripeError] = useState<string>();
  // Moved to Pix/boleto: until when the year already paid lasts (the done note says it)
  const [paidUntil, setPaidUntil] = useState<string>();

  // The billing details used last time (any Keepo), for Pix/boleto
  useEffect(() => {
    api
      .getUser()
      .then(user => {
        setBilling(current => withSavedBilling(current, user.billing, user.name));
        setBoletoBlocked(!!user.boletoBlocked);
      })
      .catch(() => undefined);
  }, []);

  if (isLoading) {
    return <LoadingModal />;
  }

  const isOneTime = method === "pix" || method === "boleto";
  const isWallet = method === "applePay" || method === "googlePay";

  const changeBilling = (next: BillingDetailsValue) => {
    setBilling(next);
    if (Object.keys(billingErrors).length) {
      setBillingErrors(validateBilling(next, method === "boleto" ? "address" : "document"));
    }
  };

  // The card is saved on Stripe (with the bank's 3D Secure when it asks), then becomes the subscription's
  const saveCard = () => {
    const errors = validateCard(card);
    setCardErrors(errors);
    const cardElement = elements?.getElement(CardNumberElement);
    if (Object.keys(errors).length || !stripe || !cardElement) {
      return;
    }
    setStripeError(undefined);
    save(async () => {
      const { clientSecret } = await api.createCardSetup(siteId);
      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement, billing_details: { name: card.name.trim() } },
      });
      if (setupError || !setupIntent?.payment_method) {
        // Stripe's message, already in the person's language
        setStripeError(setupError?.message);
        throw setupError;
      }
      await api.updatePaymentMethod(siteId, setupIntent.payment_method as string);
    });
  };

  // Yearly plan to Pix/boleto: the card stops being charged, the next year comes as a Pix/boleto
  const saveOneTime = (oneTimeMethod: "pix" | "boleto") => {
    const errors = validateBilling(billing, oneTimeMethod === "boleto" ? "address" : "document");
    setBillingErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }
    setStripeError(undefined);
    save(async () => {
      try {
        const { currentPeriodEnd } = await api.switchToOneTime(siteId, {
          method: oneTimeMethod,
          billing: { ...billing },
        });
        setPaidUntil(formatLocaleDate(currentPeriodEnd, i18n.language));
      } catch (switchError) {
        if (isApiError(switchError, "BOLETO_BLOCKED")) {
          setBoletoBlocked(true);
          setMethod(undefined);
          setStripeError(t("pages.subscription.checkout.errors.boletoBlocked"));
        }
        throw switchError;
      }
    });
  };

  const submit = () => {
    if (method === "card") {
      saveCard();
    } else if (isOneTime) {
      saveOneTime(method);
    }
  };

  // Apple Pay / Google Pay: their own button saves the wallet's card for the next charges
  const walletFields = (wallet: Wallet) => (
    <WalletSetupButton
      wallet={wallet}
      createSetup={() => api.createCardSetup(siteId)}
      onStart={() => setStripeError(undefined)}
      onCancel={() => undefined}
      onSaved={paymentMethodId => save(() => api.updatePaymentMethod(siteId, paymentMethodId))}
      onError={message => setStripeError(message || t("pages.subscription.manage.errors.generic"))}
    />
  );

  // As in the checkout's Pix/boleto: the details each needs, right under it (the name too: there's no
  // "your details" block here)
  const brazilFields = (needsAddress: boolean) => (
    <FieldGrid>
      <Field
        label={t("pages.subscription.checkout.billing.name")}
        error={billingErrors.name ? t(`pages.subscription.checkout.billing.errors.${billingErrors.name}`) : undefined}
        full>
        <input
          autoComplete="name"
          placeholder={t("pages.subscription.checkout.billing.namePlaceholder")}
          value={billing.name}
          onChange={e => changeBilling({ ...billing, name: e.target.value })}
        />
      </Field>
      <BrazilDocument value={billing} onChange={changeBilling} errors={billingErrors} />
      {needsAddress && <BrazilAddress value={billing} onChange={changeBilling} errors={billingErrors} />}
    </FieldGrid>
  );

  return (
    <Container>
      <SubHeader
        title={t(`${prefix}.title`)}
        crumbs={[
          { label: t("pages.plusBenefits.title"), to: "/keepo/plus" },
          { label: t("pages.subscriptionDetail.title"), to: detailUrl },
        ]}
      />

      <Section>
        <Fonts.Label>{t(`${prefix}.current`)}</Fonts.Label>
        <Current>
          <span className="icon">
            <CardIcon />
          </span>
          <span className="text">
            <b>{lastCard ? `•••• ${lastCard.last4}` : t("pages.subscription.checkout.methods.card.title")}</b>
            <span>{t(`${prefix}.currentDescription`)}</span>
          </span>
          <span className="badge">{t(`${prefix}.inUse`)}</span>
        </Current>
      </Section>

      <Section>
        <Fonts.Label>{t(`${prefix}.new`)}</Fonts.Label>
        <WalletProbe onAvailable={setAvailableWallets} />
        {/* The same blocks as the checkout's (not inside a panel) */}
        <PaymentMethods
          plan={isAnnual ? "ANNUALLY" : "MONTHLY"}
          isBrazil={billing.country === "BR"}
          availableWallets={availableWallets}
          isBoletoBlocked={isBoletoBlocked}
          method={method}
          onMethodChange={next => {
            setMethod(next);
            setStripeError(undefined);
          }}
          card={card}
          onCardChange={next => {
            setCard(next);
            if (Object.keys(cardErrors).length) {
              setCardErrors(validateCard(next));
            }
          }}
          cardErrors={cardErrors}
          disabled={state === "saving"}
          onSwitchToAnnual={() => navigate(`/subscription/change-plan?site=${encodeURIComponent(siteId)}`)}
          walletFields={walletFields}
          pixFields={brazilFields(false)}
          boletoFields={brazilFields(true)}
          isForRenewal
        />
      </Section>

      <Actions>
        {(stripeError || error) && (
          <p className="error" role="alert">
            {stripeError || t(`pages.subscription.manage.errors.${error}`)}
          </p>
        )}
        {state === "done" ? (
          <p className="done">
            {paidUntil
              ? t(`${prefix}.savedOneTime`, {
                  date: paidUntil,
                  method: t(`pages.subscription.checkout.methods.${method}.title`),
                })
              : t(`${prefix}.saved`)}
          </p>
        ) : (
          // The wallets save with their own button
          !isWallet && (
            <button
              type="button"
              className="primary"
              disabled={!method || state === "saving"}
              aria-busy={state === "saving"}
              onClick={submit}>
              {t(`${prefix}.save`)}
            </button>
          )
        )}
        <button type="button" className="secondary" onClick={() => navigate(detailUrl)}>
          {t("pages.subscription.manage.cancel")}
        </button>
      </Actions>
    </Container>
  );
};
